import "@/styles/globals.css";
import { Montserrat } from "next/font/google";
import PlausibleProvider from "next-plausible";
import BreadcrumbsJsonLd from "@/utils/BreadcrumbsJsonLd";
import { SpeedInsights } from "@vercel/speed-insights/next";
import { GoogleAnalytics } from "@next/third-parties/google";
import { useRouter } from "next/router";
import Nav from "@/components/nav";
import { useEffect, useState } from "react";
import Script from "next/script";
import dynamic from "next/dynamic";
import AOS from "aos";
import "aos/dist/aos.css";

const Footer = dynamic(() => import("@/components/Footer"), {
  ssr: true,
  loading: () => null,
});

const montserrat = Montserrat({
  subsets: ["latin"],
  variable: "--font-montserrat",
  display: "swap",
});

export default function App({ Component, pageProps }) {
  const router = useRouter();

  const upsChatId = "738195fb-3e79-47ce-891e-df4537bf6361";
  const siteChatId = "c906498e-e57f-4bbb-aed4-1d4277ba056a";

  // state for the currently active chat (or null)
  const [activeChat, setActiveChat] = useState(null);

  // decide which chat to activate on route change
  useEffect(() => {
    if (router.pathname === "/ups") {
      setActiveChat(upsChatId);
    } else if (router.pathname === "/contact-us") {
      setActiveChat(siteChatId);
    } else {
      setActiveChat(null);
    }
  }, [router.pathname]);

  // whenever activeChat changes, remove any existing Elfsight portal entirely
  useEffect(() => {
    const existingPortal = document.getElementById("__EAAPS_PORTAL");
    if (existingPortal) {
      existingPortal.remove();
    }
  }, [activeChat]);

  // whenever activeChat changes, toggle .hidden-elfsight on any leftover containers
  // (you can actually drop this entirely if you only want to mount/dismount the Script+div;
  //  but if you still load multiple <div class="elfsight-app-..."> tags, you may wish
  //  to keep this to clean up old hidden nodes)
  useEffect(() => {
    const allWidgets = document.querySelectorAll(
      ".portal-nest-provider__ElfsightPortalNest-sc-161fpzp-0"
    );

    allWidgets.forEach((el) => {
      if (activeChat && el.className.includes(activeChat)) {
        el.classList.remove("hidden-elfsight");
      } else {
        el.classList.add("hidden-elfsight");
      }
    });
  }, [activeChat]);

  // scroll restoration logic (unchanged)
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";

      const savedData = sessionStorage.getItem("scrollPosition");
      if (savedData) {
        const { route, scrollY } = JSON.parse(savedData);
        if (route === router.asPath) {
          window.scrollTo(0, scrollY);
        }
        sessionStorage.removeItem("scrollPosition");
      }

      const handleBeforeUnload = () => {
        sessionStorage.setItem(
          "scrollPosition",
          JSON.stringify({ route: router.asPath, scrollY: window.scrollY })
        );
      };

      window.addEventListener("beforeunload", handleBeforeUnload);
      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }
  }, [router.asPath]);

  // AOS initialization (unchanged)
  useEffect(() => {
    AOS.init({
      offset: -20,
    });
  }, []);

  return (
    <>
      <GoogleAnalytics gaId="G-H5KRCF6VZP" />
      <PlausibleProvider domain="venturawebdesign.com">
        <main className={montserrat.variable}>
          {!router.pathname.includes("/studio") &&
          !router.pathname.includes("test") ? (
            <Nav />
          ) : null}
          {!router.pathname.includes("test") ? <BreadcrumbsJsonLd /> : null}
          <Component {...pageProps} />
          {!router.pathname.includes("test") ? <SpeedInsights /> : null}
          {!router.pathname.includes("test") ? <Footer /> : null}
        </main>

        {activeChat && (
          <div key={`elfsight-${activeChat}`}>
            <Script
              src="https://static.elfsight.com/platform/platform.js"
              strategy="lazyOnload"
            />
            <div className={`elfsight-app-${activeChat}`} data-ht-app-lazy />
          </div>
        )}
      </PlausibleProvider>
    </>
  );
}
